/**
 * API Service Configuration
 * 
 * This file contains the configuration for the API service, including Axios instance setup.
 * Note: All comments and documentation in this project must be in English.
 * No Chinese characters are allowed in comments or documentation.
 */

import axios, { InternalAxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';

// For development
export const APP_API = import.meta.env.PROD 
  ? `/api` // for production 
  : `${import.meta.env.VITE_API}/api` // for development

declare module 'axios' {
    interface AxiosRequestConfig {
        skipAuth?: boolean;
        showModal?: boolean;
    }
}

// Create a custom axios instance
const api = axios.create({
    baseURL: APP_API,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add request interceptor to include authentication token
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Determine the appropriate token based on the URL
        const isAdminRequest = config.url && config.url.includes('/admin/');
        
        // For admin endpoints, use admin token
        if (isAdminRequest) {
            const adminToken = localStorage.getItem('admin_token');
            if (adminToken && config.headers) {
                config.headers.Authorization = `Bearer ${adminToken}`;
            }
        } 
        // For driver endpoints, use driver token
        else {
            const driverToken = localStorage.getItem('driver_token');
            if (driverToken && config.headers) {
                config.headers.Authorization = `Bearer ${driverToken}`;
            } 
            // Fallback to admin token if explicitly requested or for special cases
            else if (config.skipAuth !== true) {
                const adminToken = localStorage.getItem('admin_token');
                if (adminToken && config.headers) {
                    config.headers.Authorization = `Bearer ${adminToken}`;
                }
            }
        }
        
        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

// Handle authentication errors
api.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
        // Log error details for debugging
        console.error('API Error:', {
            url: error.config?.url,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message
        });
        
        // Skip redirect for registration endpoint
        const isRegistrationEndpoint = error.config && 'url' in error.config && 
            typeof error.config.url === 'string' && 
            error.config.url.includes('/driver/auth/register');
            
        const isAdminEndpoint = error.config && 'url' in error.config &&
            typeof error.config.url === 'string' &&
            error.config.url.includes('/admin/');
        
        if (error.response && error.response.status === 401) {
            if (isAdminEndpoint) {
                console.log('Admin authentication failed, redirecting to login page');
                // Remove admin token on admin auth errors
                localStorage.removeItem('admin_token');
                // Redirect to admin login page
                window.location.href = '/admin/login';
            } else if (!isRegistrationEndpoint) {
                console.log('Driver authentication failed, redirecting to login page');
                // Remove driver token on driver auth errors
                localStorage.removeItem('driver_token');
                localStorage.removeItem('token');
                // Redirect to driver login page
                window.location.href = '/driver/login';
            }
        }
        
        // Enhance error object with more readable message if available
        if (error.response?.data && typeof error.response.data === 'object' && 'error' in error.response.data) {
            const backendError = error.response.data.error;
            if (typeof backendError === 'string') {
                error.message = backendError;
            }
        }
        
        return Promise.reject(error);
    }
);

// Simple wrapper for API requests
export const requester = {
    get: async <T = unknown>(url: string, params?: Record<string, unknown>): Promise<T> => {
        const response = await api.get<T>(url, { params });
        return response.data;
    },
    
    post: async <T = unknown>(url: string, data?: Record<string, unknown>): Promise<T> => {
        const response = await api.post<T>(url, data);
        return response.data;
    },
    
    put: async <T = unknown>(url: string, data?: Record<string, unknown>): Promise<T> => {
        const response = await api.put<T>(url, data);
        return response.data;
    },
    
    delete: async <T = unknown>(url: string): Promise<T> => {
        const response = await api.delete<T>(url);
        return response.data;
    },
    
    patch: async <T = unknown>(url: string, data?: Record<string, unknown>): Promise<T> => {
        const response = await api.patch<T>(url, data);
        return response.data;
    }
};

export interface ApiResponse<T = unknown> {
    code: number;
    data: T;
    message: string;
}